/* eslint-disable indent */
import React from "react";
import UserCard from "components/atoms/UserCard";
import ControlsContainer from "components/molecules/ControlsContainer";
import { ImageDTO } from "types";
import { Span } from "utils/styling/typography.styled";
import { Badge, Card, CardContent, CardHeader, CardTitle, DescriptionText, DetailItem, DetailLabel, DetailsGrid, DetailValue, ScrollArea, Section, SectionTitle, Separator, SubSection, UserCreatedAt, UserInfoContainer, UserInfoText } from "./MetadataSection.styled";
interface MetadataSectionProps {
  imageInfo: ImageDTO | null; // Replace with the appropriate type
  toggles: {
    [key: string]: boolean;
  };
  handleToggle: (id: string) => void;
  handleShareClick: (e: React.MouseEvent, imageId?: string) => void;
  handleCompetitionsClick: (imageId?: string) => void;
  handleEditClick: (imageId?: string) => void;
  handleADetailerClick: (imageId?: string) => void;
  handleFaceSwapClick: (imageId?: string) => void;
  handleBGRemovalClick: (imageId?: string) => void;
  handleInpaintClick: (imageId?: string) => void;
  handleUpscaleClick: (imageId?: string) => void;
  prepareForDelete: (id: string) => void;
  handleVote: (id: string) => void;
  isUserImage: (username: string) => boolean;
}
const MetadataSection: React.FC<MetadataSectionProps> = ({
  imageInfo,
  toggles,
  handleToggle,
  handleShareClick,
  handleCompetitionsClick,
  handleEditClick,
  handleADetailerClick,
  handleFaceSwapClick,
  handleBGRemovalClick,
  handleInpaintClick,
  handleUpscaleClick,
  prepareForDelete,
  handleVote,
  isUserImage
}) => {
  const formatRequestType = (requestType: string) => {
    if (!requestType) return "";
    return requestType.charAt(0).toUpperCase() + requestType.slice(1).toLowerCase();
  };
  if (!imageInfo) return null;
  return <Card data-sentry-element="Card" data-sentry-component="MetadataSection" data-sentry-source-file="index.tsx">
            <CardHeader data-sentry-element="CardHeader" data-sentry-source-file="index.tsx">
                <CardTitle data-sentry-element="CardTitle" data-sentry-source-file="index.tsx">Image Metadata</CardTitle>
            </CardHeader>
            <CardContent data-sentry-element="CardContent" data-sentry-source-file="index.tsx">
                <ScrollArea data-sentry-element="ScrollArea" data-sentry-source-file="index.tsx">
                    {imageInfo.imageMetadata && <>
                            <Section>
                                <SubSection>
                                    <SectionTitle className="joyride-username">
                                        User Information
                                    </SectionTitle>
                                    <UserInfoContainer>
                                        <UserCard user={imageInfo.imageMetadata.userDTO} />
                                        <UserInfoText>
                                            <Span className="joyride-created-at">
                                                Created At:
                                            </Span>
                                            <UserCreatedAt suppressHydrationWarning>
                                                {new Date(imageInfo.createdAt).toLocaleString()}
                                            </UserCreatedAt>
                                        </UserInfoText>
                                    </UserInfoContainer>
                                </SubSection>

                                <Separator />

                                <SubSection>
                                    <SectionTitle>Image Details</SectionTitle>
                                    <DetailsGrid>
                                        <DetailItem>
                                            <DetailLabel className="joyride-image-type">
                                                Image Type
                                            </DetailLabel>
                                            <DetailValue>
                                                {formatRequestType(imageInfo.imageMetadata.requestType)}
                                            </DetailValue>
                                        </DetailItem>
                                        <DetailItem className="joyride-premium">
                                            <DetailLabel>Premium</DetailLabel>
                                            <DetailValue>
                                                <Badge variant={imageInfo.imageMetadata.premium ? "default" : "secondary"}>
                                                    {imageInfo.imageMetadata.premium ? "Yes" : "No"}
                                                </Badge>
                                            </DetailValue>
                                        </DetailItem>
                                        <DetailItem fullWidth>
                                            <DetailLabel>Prompt</DetailLabel>
                                            <DetailValue>
                                                {imageInfo.imageMetadata.theme}
                                            </DetailValue>
                                        </DetailItem>
                                    </DetailsGrid>
                                </SubSection>

                                {imageInfo.imageMetadata.npcFieldsMap && Object.entries(imageInfo.imageMetadata.npcFieldsMap).length > 0 && <>
                                            <Separator />
                                            <SubSection>
                                                <SectionTitle>
                                                    NPC Fields
                                                </SectionTitle>
                                                <DetailsGrid>
                                                    {Object.entries(imageInfo.imageMetadata.npcFieldsMap).map(([key, value]) => value && <DetailItem key={key}>
                                                                    <DetailLabel>
                                                                        {key}
                                                                    </DetailLabel>
                                                                    <DetailValue>
                                                                        {value}
                                                                    </DetailValue>
                                                                </DetailItem>)}
                                                </DetailsGrid>
                                            </SubSection>
                                        </>}

                                {imageInfo.imageMetadata.description && <>
                                        <Separator />
                                        <SubSection>
                                            <SectionTitle>
                                                Description
                                            </SectionTitle>
                                            <DescriptionText>
                                                {imageInfo.imageMetadata.description}
                                            </DescriptionText>
                                        </SubSection>
                                    </>}

                                {imageInfo.imageMetadata.modelData && <>
                                        <Separator />
                                        <SubSection>
                                            <SectionTitle className="joyride-model-info">
                                                Model Information
                                            </SectionTitle>
                                            <DetailsGrid>
                                                <DetailItem>
                                                    <DetailLabel>
                                                        Model
                                                    </DetailLabel>
                                                    <DetailValue>
                                                        {imageInfo.imageMetadata.modelData.model}
                                                    </DetailValue>
                                                </DetailItem>
                                                <DetailItem>
                                                    <DetailLabel>
                                                        CFG Scale
                                                    </DetailLabel>
                                                    <DetailValue>
                                                        {imageInfo.imageMetadata.modelData.cfg}
                                                    </DetailValue>
                                                </DetailItem>
                                                <DetailItem>
                                                    <DetailLabel>
                                                        Steps
                                                    </DetailLabel>
                                                    <DetailValue>
                                                        {imageInfo.imageMetadata.modelData.steps}
                                                    </DetailValue>
                                                </DetailItem>
                                                <DetailItem>
                                                    <DetailLabel>
                                                        Sampler
                                                    </DetailLabel>
                                                    <DetailValue>
                                                        {imageInfo.imageMetadata.modelData.sampler}
                                                    </DetailValue>
                                                </DetailItem>
                                            </DetailsGrid>
                                        </SubSection>
                                    </>}
                            </Section>
                        </>}
                </ScrollArea>

                <Separator data-sentry-element="Separator" data-sentry-source-file="index.tsx" />

                <Section data-sentry-element="Section" data-sentry-source-file="index.tsx">
                    <ControlsContainer imageInfo={imageInfo} toggles={toggles} handleToggle={handleToggle} handleShareClick={handleShareClick} handleCompetitionsClick={handleCompetitionsClick} handleEditClick={handleEditClick} handleADetailerClick={handleADetailerClick} handleFaceSwapClick={handleFaceSwapClick} handleBGRemovalClick={handleBGRemovalClick} handleInpaintClick={handleInpaintClick} handleUpscaleClick={handleUpscaleClick} prepareForDelete={prepareForDelete} handleVote={handleVote} isUserImage={isUserImage} data-sentry-element="ControlsContainer" data-sentry-source-file="index.tsx" />
                </Section>
            </CardContent>
        </Card>;
};
export default React.memo(MetadataSection);